import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { FiClock, FiMessageCircle, FiBookmark, FiHeart, FiShare2 } from 'react-icons/fi';
import { MdVerified } from 'react-icons/md';
import { CATEGORIES } from '../../utils/constants';
import { useAuth } from '../../context/AuthContext';
import { favoritesAPI, reactionsAPI } from '../../services/api';
import { resolveMediaUrl } from '../../utils/media';
import LoginPrompt from '../LoginPrompt/LoginPrompt';
import toast from 'react-hot-toast';
import './PostCard.css';

const PostCard = ({ post, variant = 'default', isFavorited: initialFavorited = false, onRemoveFavorite }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated } = useAuth();
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const [userReaction, setUserReaction] = useState(null);
  const [reactionCounts, setReactionCounts] = useState(post.reactions || {});
  const [isSaved, setIsSaved] = useState(initialFavorited);
  const category = CATEGORIES.find(cat => cat.id === post.category);

  useEffect(() => {
    setIsSaved(initialFavorited);
  }, [initialFavorited]);

  useEffect(() => {
    setReactionCounts(post.reactions || {});
  }, [post.reactions]);

  useEffect(() => {
    // Check if post is favorited on mount
    const checkFavorited = async () => {
      if (isAuthenticated && post.id) {
        try {
          const response = await favoritesAPI.check(post.id);
          setIsSaved(response.data.isFavorited);
        } catch (error) {
          console.error('Error checking favorite status:', error);
        }
      }
    };

    checkFavorited();
  }, [isAuthenticated, post.id]);

  const slugify = (text) => {
    if (!text) return '';
    return text
      .toString()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  };

  const getPostSlug = () => {
    if (post?.slug) return post.slug;
    const base = slugify(post?.title) || 'post';
    // Fallback is descriptive (not numeric-only) and backend can resolve it.
    return post?.id ? `${base}-${post.id}` : base;
  };

  const openArticle = () => {
    const slug = getPostSlug();
    navigate(`/article/${slug}`, { state: { backgroundLocation: location } });
  };
  
  const formatDate = (date) => {
    const postDate = new Date(date);
    const now = new Date();
    const diffTime = Math.abs(now - postDate);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Hoy';
    if (diffDays === 1) return 'Ayer';
    if (diffDays < 7) return `Hace ${diffDays} días`;
    if (diffDays < 30) return `Hace ${Math.floor(diffDays / 7)} semanas`;
    return postDate.toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const handleCardClick = (e) => {
    // Don't open modal if clicking on links or buttons
    if (e.target.closest('a') || e.target.closest('button')) {
      return;
    }
    // Open via URL (modal overlay handled by App routing)
    openArticle();
  };

  const handleQuickView = (e) => {
    e.stopPropagation();
    openArticle();
  };

  return (
    <>
      <article className={`post-card post-card-${variant}`} onClick={handleCardClick} style={{ cursor: 'pointer' }}>
        <div className="post-card-image-wrapper">
          <div className="post-card-image">
            <img 
              src={resolveMediaUrl(post.image) || 'https://images.unsplash.com/photo-1532012197267-da84d127e765?w=400'} 
              alt={post.title}
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = 'https://images.unsplash.com/photo-1532012197267-da84d127e765?w=400';
              }}
            />
            {category && (
              <div className="post-card-category" style={{ background: category.color }}>
                <span>{category.icon}</span>
                <span>{category.name}</span>
              </div>
            )}
            <div className="image-overlay" onClick={handleQuickView}>
              <span className="view-full">Vista rápida</span>
            </div>
          </div>
        </div>
        
        <div className="post-card-content">
          <Link to={`/article/${getPostSlug()}`} state={{ backgroundLocation: location }} className="post-card-link" onClick={(e) => e.stopPropagation()}>
            <h3 className="post-card-title">
              {post.title}
            </h3>
          </Link>
          
          <p className="post-card-description">
            {post.abstract 
              ? (post.abstract.length > 150 ? post.abstract.substring(0, 150) + '...' : post.abstract)
              : (post.excerpt || post.description)
            }
          </p>
          
          <div className="post-card-footer">
            <div className="post-card-author">
              <img 
                src={resolveMediaUrl(post.author?.avatar) || '/default-avatar.png'} 
                alt={post.author?.name}
                className="author-avatar"
              />
              <div className="author-info">
                <Link
                  to={`/${post.author?.username || post.author?.id}`}
                  className="author-name"
                  onClick={(e) => e.stopPropagation()}
                >
                  {post.author?.name}
                  {post.author?.verification_level && post.author.verification_level !== 'none' && (
                    <MdVerified
                      style={{
                        marginLeft: 6,
                        verticalAlign: 'middle',
                        color: post.author.badge_color || 'currentColor'
                      }}
                      title={post.author.badge_label || 'Cuenta verificada'}
                    />
                  )}
                </Link>
                <div className="post-card-meta">
                  <span className="meta-item">
                    <FiClock />
                    {formatDate(post.createdAt)}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="post-actions">
            <button 
              className={`action-button ${userReaction === 'love' ? 'active' : ''}`}
              onClick={(e) => {
                e.stopPropagation();
                if (!isAuthenticated) {
                  setShowLoginPrompt(true);
                  return;
                }
                (async () => {
                  try {
                    const res = await reactionsAPI.toggle(post.id, 'love');
                    setUserReaction(res.data?.userReaction || null);
                    setReactionCounts(res.data?.reactions || {});
                  } catch (error) {
                    console.error('Error toggling reaction:', error);
                    toast.error('No se pudo reaccionar');
                  }
                })();
              }}
            >
              <FiHeart />
              <span>{reactionCounts?.love || 0}</span>
            </button>

            <button className="action-button" onClick={(e) => {
              e.stopPropagation();
              if (!isAuthenticated) {
                setShowLoginPrompt(true);
                return;
              }
              openArticle();
            }}>
              <FiMessageCircle />
              <span>{post.comments || 0}</span>
            </button>

            <button 
              className={`action-button ${isSaved ? 'active' : ''}`}
              onClick={async (e) => {
                e.stopPropagation();
                if (!isAuthenticated) {
                  setShowLoginPrompt(true);
                  return;
                }
                
                try {
                  if (isSaved) {
                    await favoritesAPI.remove(post.id);
                    setIsSaved(false);
                    toast.success('Eliminado de favoritos');
                    if (onRemoveFavorite) {
                      onRemoveFavorite(post.id);
                    }
                  } else {
                    await favoritesAPI.add(post.id);
                    setIsSaved(true);
                    toast.success('Agregado a favoritos');
                  }
                } catch (error) {
                  console.error('Error toggling favorite:', error);
                  toast.error('Error al actualizar favoritos');
                }
              }}
            >
              <FiBookmark fill={isSaved ? 'currentColor' : 'none'} />
            </button>

            <button className="action-button" onClick={(e) => {
              e.stopPropagation();
              const url = `${window.location.origin}/article/${getPostSlug()}`;
              if (navigator.share) {
                navigator.share({ title: post.title, url });
              } else {
                navigator.clipboard.writeText(url);
              }
            }}>
              <FiShare2 />
            </button>
          </div>
        </div>
      </article>

      {showLoginPrompt && (
        <LoginPrompt 
          message="Inicia sesión para interactuar con este artículo"
          onClose={() => setShowLoginPrompt(false)}
        />
      )}
    </>
  );
};

export default PostCard;
